// src/ConsumerApp/Screens/Account/views/InvoicesView.js

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../assets/theme/theme';

import NoteIcon from '../../../../assets/icons/Note.svg';
import DownloadSimple from '../../../../assets/icons/DownloadSimple.svg';
import FileTextIcon from '../../../../assets/icons/FileText.svg';
import ArrowLeft from '../../../../assets/icons/ArrowLeft.svg';

import { useGetCustomerInvoices, useDownloadInvoice } from '../../../api/invoices/invoice.hooks';

export default function InvoicesView() {
  const navigate = useNavigate();
  const [downloadingId, setDownloadingId] = useState(null);

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;
  const token = customer?.token || sessionStorage.getItem('@auth_token') || localStorage.getItem('@auth_token');

  const { data: invoices = [], isLoading } = useGetCustomerInvoices(customerId);
  const downloadMutation = useDownloadInvoice();

  const formatDate = (isoString) => {
    if (!isoString) return 'Date unavailable';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleDownload = async (item) => {
    const files = item.files || [];
    if (!files || files.length === 0) {
      alert(`No PDF file attached to invoice ${item.invoice_number || ''}.`);
      return;
    }

    setDownloadingId(item._id);
    try {
      await downloadMutation.mutateAsync({
        fileRelativePath: files[0],
        invoiceNumber: item.invoice_number || 'Invoice',
        token,
      });
    } catch (err) {
      alert(err.message || 'Could not download invoice. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div>
      <style>{`
        .bb-sub-circle-back-btn {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid #E8E0D0;
          background-color: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
          margin-right: 12px;
          outline: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          transition: transform 0.12s ease, background-color 0.12s ease;
          box-sizing: border-box;
        }
        .bb-sub-circle-back-btn:active {
          transform: scale(0.92);
          background-color: #F8FAFC;
        }
        .bb-sub-circle-back-btn img {
          width: 20px;
          height: 20px;
          display: block;
        }
        .bb-sub-mobile-header {
          display: none;
        }
        .bb-sub-desktop-header {
          display: flex;
        }
        @media (max-width: 900px) {
          .bb-sub-desktop-header {
            display: none !important;
          }
          .bb-sub-mobile-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 56px;
            padding: 0 16px;
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            box-sizing: border-box;
            position: sticky;
            top: 0;
            z-index: 1000;
          }
          .bb-sub-mobile-content {
            padding: 14px 16px 36px 16px !important;
          }
        }
      `}</style>

      {/* Mobile Edge-to-Edge Bar */}
      <div className="bb-sub-mobile-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            className="bb-sub-circle-back-btn"
            onClick={() => navigate('/consumer/account', { replace: true })}
            aria-label="Back to Account"
          >
            <img src={ArrowLeft} alt="Back" />
          </button>
          <h2 style={{
            fontFamily: theme.typography.headingLG.fontFamily,
            fontSize: 18,
            fontWeight: '800',
            color: theme.colors.charcoal,
            margin: 0,
          }}>
            Invoices & Receipts
          </h2>
        </div>
      </div>

      <div style={styles.container} className="bb-sub-mobile-content">
        {/* Desktop Header */}
        <div style={styles.pageHeader} className="bb-sub-desktop-header">
          <div>
            <h2 style={styles.headerTitle}>Invoices & Receipts</h2>
            <span style={styles.headerSubtitle}>
              Download GST-compliant tax invoices and billing receipts
            </span>
          </div>
        </div>

        {isLoading ? (
          <div style={styles.loadingBox}>Fetching tax invoices...</div>
        ) : invoices.length === 0 ? (
          <div style={styles.emptyBox}>
            <div style={styles.emptyIconCircle}>
              <img src={FileTextIcon} alt="invoices" style={{ width: 22, height: 22, opacity: 0.4 }} />
            </div>
            <h4 style={styles.emptyTitle}>No invoices available</h4>
            <p style={styles.emptySubtitle}>
              Tax invoices are generated once materials are billed and dispatched to your site.
            </p>
          </div>
        ) : (
          <div style={styles.cardContainer}>
            <div style={styles.cardHeader}>
              <span style={styles.cardHeaderLabel}>Invoice Summary</span>
              <span style={styles.countBadge}>{invoices.length} invoices</span>
            </div>

            <div style={styles.invoicesList}>
              {invoices.map((item, index) => {
                const isLast = index === invoices.length - 1;
                const isItemDownloading = downloadingId === item._id;

                return (
                  <div
                    key={item._id || index}
                    style={{
                      ...styles.itemRow,
                      borderBottom: isLast ? 'none' : '1px solid #E2E8F0',
                    }}
                  >
                    <div style={styles.leftCol}>
                      <div style={styles.iconFrame}>
                        <img src={NoteIcon} alt="note" style={styles.noteSvg} />
                      </div>

                      <div style={styles.detailsCol}>
                        <span style={styles.orderIdText}>{item.invoice_number || 'Tax Invoice'}</span>
                        <span style={styles.dateTimeText}>{formatDate(item.invoice_date)}</span>
                      </div>
                    </div>

                    <div style={styles.rightCol}>
                      <span style={styles.amountText}>{formatAmount(item.total)}</span>

                      <button
                        type="button"
                        style={styles.downloadBtn}
                        onClick={() => handleDownload(item)}
                        disabled={isItemDownloading}
                      >
                        <img
                          src={DownloadSimple}
                          alt="download"
                          style={{
                            width: 15,
                            height: 15,
                            filter:
                              'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
                          }}
                        />
                        <span style={styles.downloadText}>
                          {isItemDownloading ? 'Downloading...' : 'Download'}
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    ...theme.typography.headingLG,
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: theme.colors.charcoal,
  },
  headerSubtitle: {
    ...theme.typography.bodySM,
    fontSize: 13,
    color: theme.colors.concrete,
    marginTop: 4,
    display: 'block',
  },
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    backgroundColor: '#F8F6F2',
    borderBottom: '1px solid #CBD5E1',
  },
  cardHeaderLabel: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 13,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  countBadge: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 11.5,
    fontWeight: '600',
    color: theme.colors.concrete,
  },
  invoicesList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
  },
  leftCol: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    minWidth: 0,
  },
  iconFrame: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  noteSvg: {
    width: 20,
    height: 20,
    filter:
      'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)',
  },
  detailsCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  orderIdText: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 14.5,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  dateTimeText: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 12,
    color: theme.colors.concrete,
  },
  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 6,
    flexShrink: 0,
  },
  amountText: {
    fontFamily: theme.typography.bodyMD.fontFamily,
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  downloadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    outline: 'none',
  },
  downloadText: {
    fontFamily: theme.typography.bodySM.fontFamily,
    fontSize: 12.5,
    fontWeight: '700',
    color: theme.colors.primaryRed || '#C7061F',
  },
  loadingBox: {
    padding: '50px 0',
    textAlign: 'center',
    color: theme.colors.concrete,
    fontSize: 13.5,
    fontFamily: theme.typography.bodyMD.fontFamily,
  },
  emptyBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px dashed #CBD5E1',
    padding: '48px 24px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  emptyTitle: {
    ...theme.typography.headingMD,
    margin: 0,
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.charcoal,
  },
  emptySubtitle: {
    ...theme.typography.bodySM,
    margin: '4px 0 0 0',
    fontSize: 12.5,
    color: theme.colors.concrete,
    maxWidth: 320,
    lineHeight: '18px',
  },
};